import React, { FC, Fragment, ReactNode, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import {
    Alert,
    Box,
    Button,
    Chip,
    Grid,
    Link,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import PageTitle from 'components/shared/page-title/page-title.component';
import Loader from 'components/shared/loader/loader.component';
import LogInAsOrganizationUserDialog from 'components/orders/log-in-as-organization-user-dialog.component';
import { formatDate, formatDateTime, restorationTypeLabel, statusLabel } from 'components/orders/order-format';
import { adminOrderTemplate } from 'apollo/templates/orders';
import { AdminOrderDetails, AdminOrderOrganization } from 'models/admin-order';

const Section: FC<{ title: string; children: ReactNode }> = ({ title, children }) => (
    <Paper sx={{ p: 2, height: '100%' }}>
        <Typography variant="h3" mb={1.5}>
            {title}
        </Typography>
        {children}
    </Paper>
);

const Field: FC<{ label: string; children: ReactNode }> = ({ label, children }) => (
    <Box display="flex" gap={2} py={0.5}>
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 150 }}>
            {label}
        </Typography>
        <Typography variant="body2" component="div">
            {children ?? '–'}
        </Typography>
    </Box>
);

const OrderDetails: FC = () => {
    const { t: translate } = useTranslation(['super-admin', 'orders']);
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [logInAs, setLogInAs] = useState<AdminOrderOrganization>();
    const { data, loading, error } = useQuery<{ adminOrder: AdminOrderDetails | null }>(adminOrderTemplate, {
        variables: { id },
        fetchPolicy: 'network-only',
    });

    if (loading) return <Loader />;
    if (error) return <Alert severity="error">{error.message}</Alert>;
    const order = data?.adminOrder;
    if (!order) return <Alert severity="warning">{translate('super-admin:ORDER_NOT_FOUND')}</Alert>;

    const party = (organization?: AdminOrderOrganization) =>
        organization ? (
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <span>
                    {organization.name} {organization.type ? `(${organization.type})` : ''}
                </span>
                <Button size="small" variant="text" onClick={() => setLogInAs(organization)}>
                    {translate('super-admin:LOG_IN_AS')}
                </Button>
            </Box>
        ) : undefined;

    const orderLink = (link: { _id: string; orderId?: string; type?: string }) => (
        <Link component={RouterLink} to={`/orders/${link._id}`} key={link._id} sx={{ mr: 1 }}>
            {link.orderId ?? link._id}
            {link.type ? ` (${link.type})` : ''}
        </Link>
    );

    return (
        <Fragment>
            <PageTitle
                headingStyle="h1"
                title={`${translate('super-admin:ORDER')} ${order.orderId ?? ''}`}
                showBackButton
                onBackArrow={() => navigate(-1)}
            />
            <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                <Chip label={statusLabel(translate, order.status, order.rejectStatus)} />
                {order.isDraft ? <Chip variant="outlined" label={translate('super-admin:ORDER_DRAFT')} /> : undefined}
                {order.isPaused ? <Chip variant="outlined" label={translate('super-admin:ORDER_PAUSED')} /> : undefined}
                {order.isWarrantyOrder ? (
                    <Chip variant="outlined" label={translate('super-admin:ORDER_WARRANTY')} />
                ) : undefined}
                {order.isCopyOrder ? (
                    <Chip variant="outlined" label={translate('super-admin:ORDER_COPY')} />
                ) : undefined}
                {order.isImportedOrder ? (
                    <Chip variant="outlined" label={translate('super-admin:ORDER_IMPORTED')} />
                ) : undefined}
            </Box>
            <Alert severity="info" sx={{ mb: 2 }}>
                {translate('super-admin:ORDER_READ_ONLY_INFO')}
            </Alert>

            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <Section title={translate('super-admin:ORDER_PARTIES')}>
                        <Field label={translate('super-admin:ORDER_CUSTOMER')}>{party(order.customer)}</Field>
                        <Field label={translate('super-admin:ORDER_SUPPLIER')}>{party(order.supplier)}</Field>
                        {order.clinic && order.clinic._id !== order.customer?._id ? (
                            <Field label={translate('super-admin:ORDER_CLINIC')}>{order.clinic.name}</Field>
                        ) : undefined}
                        {order.lab && order.lab._id !== order.supplier?._id ? (
                            <Field label={translate('super-admin:ORDER_LAB')}>{order.lab.name}</Field>
                        ) : undefined}
                        <Field label={translate('super-admin:ORDER_CREATED_BY')}>
                            {order.createdBy
                                ? `${order.createdBy.fullName ?? ''} ${order.createdBy.email ?? ''}`
                                : undefined}
                        </Field>
                        <Field label={translate('super-admin:ORDER_DENTIST')}>
                            {order.dentist ? `${order.dentist.fullName ?? ''} ${order.dentist.email ?? ''}` : undefined}
                        </Field>
                    </Section>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Section title={translate('super-admin:ORDER_SUMMARY')}>
                        <Field label={translate('super-admin:ORDER_PATIENT')}>
                            {order.patient
                                ? `${order.patient.firstName ?? ''} ${order.patient.lastName ?? ''} ${
                                      order.patient.personalIdentityNumber ?? ''
                                  }`
                                : undefined}
                        </Field>
                        <Field label={translate('super-admin:ORDER_INTERNAL_ID')}>{order.internalOrderId}</Field>
                        <Field label={translate('super-admin:ORDER_VENDOR_REFERENCE')}>{order.vendorReference}</Field>
                        <Field label={translate('super-admin:ORDER_CREATED')}>{formatDateTime(order.createdOn)}</Field>
                        <Field label={translate('super-admin:ORDER_UPDATED')}>{formatDateTime(order.updatedOn)}</Field>
                        <Field label={translate('super-admin:ORDER_DELIVERY_DATE')}>
                            {formatDate(order.deliveryDate)}
                        </Field>
                        <Field label={translate('super-admin:ORDER_STEP')}>{order.currentStep}</Field>
                        <Field label={translate('super-admin:ORDER_INVOICING')}>{order.invoicingState}</Field>
                        {order.invoiceId ? (
                            <Field label={translate('super-admin:INVOICE')}>
                                <Link
                                    component={RouterLink}
                                    to={`/invoices/${order.invoiceId}`}
                                    data-test="order.invoice"
                                >
                                    {order.invoiceNumber ?? order.invoiceId}
                                </Link>
                            </Field>
                        ) : undefined}
                        <Field label={translate('super-admin:ORDER_VERSION')}>{order.version}</Field>
                        {order.orderRef ? (
                            <Field label={translate('super-admin:ORDER_CREATED_FROM')}>
                                {orderLink(order.orderRef)}
                            </Field>
                        ) : undefined}
                        {order.linkedOrders.length ? (
                            <Field label={translate('super-admin:ORDER_LINKED_ORDERS')}>
                                {order.linkedOrders.map(orderLink)}
                            </Field>
                        ) : undefined}
                    </Section>
                </Grid>

                <Grid item xs={12}>
                    <Section title={translate('super-admin:ORDER_CONSTRUCTIONS')}>
                        {order.restorations.length === 0 ? (
                            <Typography variant="body2">{translate('super-admin:ORDER_NO_CONSTRUCTIONS')}</Typography>
                        ) : (
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{translate('super-admin:ORDER_CONSTRUCTION')}</TableCell>
                                        <TableCell>{translate('super-admin:ORDER_TEETH')}</TableCell>
                                        <TableCell>{translate('super-admin:ORDER_ACTIONS')}</TableCell>
                                        <TableCell>{translate('super-admin:ORDER_MATERIALS')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {order.restorations.map((restoration, index) => (
                                        <TableRow key={index}>
                                            <TableCell>
                                                {restorationTypeLabel(translate, restoration.type)}
                                                {restoration.warranty
                                                    ? ` (${translate('super-admin:ORDER_WARRANTY')})`
                                                    : ''}
                                            </TableCell>
                                            <TableCell>{restoration.teeth ?? '–'}</TableCell>
                                            <TableCell>{restoration.actions.join(', ') || '–'}</TableCell>
                                            <TableCell>{restoration.materials.join(', ') || '–'}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                        {order.additionalInfo ? (
                            <Box mt={2}>
                                <Field label={translate('super-admin:ORDER_ADDITIONAL_INFO')}>
                                    {order.additionalInfo}
                                </Field>
                            </Box>
                        ) : undefined}
                    </Section>
                </Grid>

                <Grid item xs={12}>
                    <Section title={translate('super-admin:ORDER_HISTORY')}>
                        {order.events.length === 0 ? (
                            <Typography variant="body2">{translate('super-admin:ORDER_NO_HISTORY')}</Typography>
                        ) : (
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{translate('super-admin:ORDER_EVENT_DATE')}</TableCell>
                                        <TableCell>{translate('super-admin:ORDER_EVENT')}</TableCell>
                                        <TableCell>{translate('super-admin:ORDER_EVENT_USER')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {order.events.map((event, index) => (
                                        <TableRow key={index}>
                                            <TableCell>{formatDateTime(event.date)}</TableCell>
                                            <TableCell>
                                                {event.type}
                                                {event.message ? ` – ${event.message}` : ''}
                                            </TableCell>
                                            <TableCell>{event.user?.fullName ?? '–'}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </Section>
                </Grid>
            </Grid>

            {logInAs ? (
                <LogInAsOrganizationUserDialog
                    organization={logInAs}
                    path={`/order-details/${order._id}/main-view`}
                    onClose={() => setLogInAs(undefined)}
                />
            ) : undefined}
        </Fragment>
    );
};

export default OrderDetails;
